<template>
  <div class="screenshot-slot">
    <font-awesome-icon icon="image" class="screenshot-slot-icon" aria-hidden="true" />
    <span class="screenshot-slot-label">{{ t('Screenshot') }}</span>
  </div>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* Where a screenshot goes, before the screenshot has arrived.

   It was written inside the browser frame, and the device band needed the same
   thing: two places on a case page can be waiting for a picture, and both have
   to say so the same way. The frame's empty view and a device with no capture
   are one state, so they are one component and the word is translated once.

   It fills its containing block rather than sizing itself, which is the whole of
   what it decides. A slot is not an object: it is the shape of an absence, and
   the box it is absent from belongs to the caller. The frame insets it in the
   window it has left (because the chrome around it is still the frame's) and the
   device band runs it to the edge of the device's own box (because there is no
   device there, only the space one would stand in). Both callers therefore
   position it, and this only fills what it is put in.

   The dashed rule is the one place on the site that uses one, and it is here
   because a dashed box is the mark every reader already knows means "a picture
   goes here"; a solid rule would read as a panel with nothing in it.

   The type is the site's small uppercase label, with one addition: the label
   scales with the slot's own width instead of being pinned to `$label-size`.
   This is the only component on the site whose box can be 90px wide or 600px
   wide depending on who rendered it, and the word is the same ten characters in
   both. `9cqi` is chosen so the ten characters always come to about four fifths
   of the box: at the width where the token is the smaller of the two the label
   is exactly the token, which is every slot wide enough to read it at, and below
   that it shrinks rather than wrapping. `nowrap` then guarantees the word is
   never broken, and the sizing is what makes that safe rather than a gamble.

   The icon scales with the slot for the same reason, so the mark and the word
   stay in proportion instead of a fixed mark sitting over a shrunken word. */
.screenshot-slot {
  align-items: center;
  border: 1px dashed $line-blue;
  border-radius: $r-sm;
  color: $text-muted;
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  font-family: $headings-font-family;
  font-weight: $label-weight;
  gap: 0.7rem;
  inset: 0;
  justify-content: center;
  letter-spacing: $label-tracking;
  position: absolute;
  text-align: center;
  text-transform: uppercase;
}

.screenshot-slot-icon {
  font-size: min(1.5rem, 22cqi);
  opacity: 0.55;
}

.screenshot-slot-label {
  font-size: min(#{$label-size}, 9cqi);
  white-space: nowrap;
}
</style>

<script>
import { faImage } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';

FontAwesomeLibrary.add(faImage);

export default {
  setup() {
    /* The slot's own copy, looked up here rather than passed in, for the reason
       the frame and the viewer declare their own chrome: a caller that is
       waiting for a picture does not have to think of a word for the wait, and
       the two places that wait say it the same way because they read the same
       key. */
    const { t } = useI18n({
      messages: {
        'en-MY': {
          Screenshot: 'Screenshot'
        },
        'zh-MY': {
          Screenshot: '截图'
        },
        'ms-MY': {
          Screenshot: 'Tangkapan skrin'
        }
      }
    });

    return { t };
  }
};
</script>
